// File: apps/web/src/lib/signalr/importHub.ts
// Version: 2026.4.7 | Sprint 7 Wave 3 — SignalR client for the import progress hub
//
// CONNECTION LIFECYCLE
//
// We open ONE HubConnection per useImportProgress hook instance, scoped
// to one batchId. The connection auto-reconnects with exponential backoff
// on transient failures (transport drop, server restart, app sleep).
//
// On mount:
//   1. Build the connection with accessTokenFactory wired to useAuth().getAccessToken
//   2. Register handlers for batchStarted/rowClassified/batchCompleted/batchFailed
//   3. Start the connection
//   4. Invoke SubscribeToBatch(batchId)
//
// On unmount:
//   1. Invoke UnsubscribeFromBatch(batchId)  (best-effort, swallow errors)
//   2. Stop the connection
//
// STATE MACHINE
//
//   idle        — no active batch (batchId is null)
//   connecting  — opening the SignalR transport
//   subscribed  — connected and subscribed, waiting for first message
//   in-progress — at least one row processed
//   completed   — batchCompleted received
//   failed      — batchFailed received OR connection failed
//
// The hook returns the current state plus the last summary or error.
// Components can drive their own UI off this state without having to
// manage SignalR primitives directly.

import { useEffect, useRef, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '@forgetrack/auth';
import { requiredEnv } from '@forgetrack/api-client';
import type {
  BatchStartedMessage,
  RowClassifiedMessage,
  BatchCompletedMessage,
  BatchFailedMessage,
} from '../api/types-import';

// ─── Hub URL ───────────────────────────────────────────────────────────────

const API_BASE = requiredEnv('VITE_API_URL', import.meta.env.VITE_API_URL).replace(/\/$/, '');
const HUB_URL  = `${API_BASE}/hubs/import`;

// ─── State machine ────────────────────────────────────────────────────────

export type ImportProgressState =
  | 'idle'
  | 'connecting'
  | 'subscribed'
  | 'in-progress'
  | 'completed'
  | 'failed';

export interface ImportProgress {
  state:        ImportProgressState;
  totalRows:    number;
  completed:    number;
  succeeded:    number;
  failed:       number;
  lastRowId:    number | null;
  lastRowStatus: 'Classified' | 'Failed' | null;
  summary:      BatchCompletedMessage | null;
  error:        string | null;
}

const INITIAL_PROGRESS: ImportProgress = {
  state:        'idle',
  totalRows:    0,
  completed:    0,
  succeeded:    0,
  failed:       0,
  lastRowId:    null,
  lastRowStatus: null,
  summary:      null,
  error:        null,
};

// ─── Connection factory (pure — no React, easy to test in isolation) ──────

export function createImportHubConnection(
  accessTokenFactory: () => string | Promise<string>
): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(HUB_URL, {
      // SignalR will call this on initial connect AND on every reconnect
      // attempt, so token expiry across long-running batches is handled
      // for free as long as MSAL refreshes silently in the background.
      accessTokenFactory,
    })
    .withAutomaticReconnect({
      // Custom backoff: try fast a couple times, then back off.
      // null = give up. The hook surfaces this as state='failed'.
      nextRetryDelayInMilliseconds: ctx => {
        if (ctx.previousRetryCount === 0) return 0;       // immediate
        if (ctx.previousRetryCount === 1) return 2_000;
        if (ctx.previousRetryCount === 2) return 5_000;
        if (ctx.previousRetryCount < 10)  return 15_000;
        return null;
      },
    })
    .configureLogging(
      // Quieter than Information so the dev console doesn't drown.
      // Bump to LogLevel.Information when debugging.
      LogLevel.Warning
    )
    .build();
}

// ─── Hook ──────────────────────────────────────────────────────────────────

/**
 * Subscribe to a single import batch and reflect its progress as React state.
 * Pass null/undefined for batchId to disable the connection (e.g. before an
 * upload has started).
 */
export function useImportProgress(batchId: string | null | undefined): ImportProgress {
  const { getAccessToken } = useAuth();
  const [progress, setProgress] = useState<ImportProgress>(INITIAL_PROGRESS);

  // Hold the live connection in a ref so the effect can clean it up
  // even if React re-runs for unrelated state changes.
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!batchId) {
      setProgress(INITIAL_PROGRESS);
      return;
    }

    // Reset progress when batchId changes (new batch = fresh state)
    setProgress({ ...INITIAL_PROGRESS, state: 'connecting' });

    let cancelled = false;
    const connection = createImportHubConnection(async () => {
      try {
        return (await getAccessToken()) ?? '';
      } catch {
        return '';
      }
    });
    connectionRef.current = connection;

    // ── Register handlers BEFORE start so we don't miss any early messages ──
    // The C# notifier broadcasts these method names exactly:
    //   batchStarted, rowClassified, batchCompleted, batchFailed
    connection.on('batchStarted', (msg: BatchStartedMessage) => {
      if (cancelled) return;
      setProgress(prev => ({
        ...prev,
        state:     'subscribed',
        totalRows: msg.totalRows,
      }));
    });

    connection.on('rowClassified', (msg: RowClassifiedMessage) => {
      if (cancelled) return;
      setProgress(prev => ({
        ...prev,
        state:        'in-progress',
        totalRows:    msg.totalCount,
        completed:    msg.completedCount,
        succeeded:    prev.succeeded + (msg.status === 'Classified' ? 1 : 0),
        failed:       prev.failed    + (msg.status === 'Failed'     ? 1 : 0),
        lastRowId:    msg.stagedId,
        lastRowStatus: msg.status,
      }));
    });

    connection.on('batchCompleted', (msg: BatchCompletedMessage) => {
      if (cancelled) return;
      setProgress(prev => ({
        ...prev,
        state:     'completed',
        completed: msg.totalRows,
        totalRows: msg.totalRows,
        succeeded: msg.classifiedRows,
        failed:    msg.failedRows,
        summary:   msg,
      }));
    });

    connection.on('batchFailed', (msg: BatchFailedMessage) => {
      if (cancelled) return;
      setProgress(prev => ({
        ...prev,
        state: 'failed',
        error: msg.error,
      }));
    });

    connection.onreconnecting(() => {
      if (cancelled) return;
      setProgress(prev => ({ ...prev, state: 'connecting' }));
    });

    connection.onreconnected(() => {
      if (cancelled) return;
      // Re-subscribe — the server side stores group memberships in memory
      // and a reconnect creates a new connection ID.
      connection.invoke('SubscribeToBatch', batchId).catch(() => { /* swallow */ });
      setProgress(prev => ({
        ...prev,
        state: prev.completed > 0 ? 'in-progress' : 'subscribed',
      }));
    });

    connection.onclose(err => {
      if (cancelled) return;
      // onclose fires after both clean stops AND giving up on reconnects.
      // Distinguish using the previous state.
      setProgress(prev => {
        if (prev.state === 'completed' || prev.state === 'failed') return prev;
        return {
          ...prev,
          state: 'failed',
          error: err?.message ?? 'Connection lost',
        };
      });
    });

    // ── Start ─────────────────────────────────────────────────────────────
    (async () => {
      try {
        await connection.start();
        if (cancelled) return;
        await connection.invoke('SubscribeToBatch', batchId);
        if (cancelled) return;
        setProgress(prev => ({
          ...prev,
          state: prev.totalRows > 0 ? 'in-progress' : 'subscribed',
        }));
      } catch (err: any) {
        if (cancelled) return;
        setProgress(prev => ({
          ...prev,
          state: 'failed',
          error: err?.message ?? 'Failed to connect to import hub',
        }));
      }
    })();

    // ── Cleanup ───────────────────────────────────────────────────────────
    return () => {
      cancelled = true;
      const conn = connectionRef.current;
      connectionRef.current = null;

      if (!conn) return;
      // Best-effort unsubscribe; swallow if the connection is already gone
      if (conn.state === HubConnectionState.Connected) {
        conn.invoke('UnsubscribeFromBatch', batchId).catch(() => { /* swallow */ });
      }
      conn.stop().catch(() => { /* swallow */ });
    };
    // We intentionally re-run when batchId changes — that's the whole point.
    // getAccessToken is stable across renders in @forgetrack/auth's MSAL impl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  return progress;
}
